import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Plus, Search, AlertCircle, CheckCircle, X, Loader2, FileText, ArrowLeft, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const FeeStructureItems = () => {
  const { api, user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [feeItems, setFeeItems] = useState([]);
  const [feeStructure, setFeeStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFeeItem, setSelectedFeeItem] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    amount: '',
    is_mandatory: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Fetch fee structure details and items
  const fetchFeeStructureAndItems = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/v1/fee-structures/${id}/school/${user.school_id}`);
      
      // Assuming the response contains both structure info and items
      // Adjust based on your actual API response structure
      if (Array.isArray(response.data)) {
        setFeeItems(response.data);
      } else {
        setFeeStructure(response.data.structure);
        setFeeItems(response.data.items || []);
      }
    } catch (error) {
      showNotification('Failed to fetch fee items', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeeStructureAndItems();
  }, [id]);

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormErrors({});

    try {
      await api.post(`/api/v1/fee-structures/${id}/school/${user.school_id}/create-fee-item`, {
        ...formData,
        amount: parseFloat(formData.amount)
      });
      showNotification('Fee item created successfully', 'success');
      setShowCreateModal(false);
      resetForm();
      fetchFeeStructureAndItems();
    } catch (error) {
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormErrors({});

    try {
      await api.put(`/api/v1/fee-structures/${id}/school/${user.school_id}/item/${selectedFeeItem.id}/update`, {
        ...formData,
        amount: parseFloat(formData.amount)
      });
      showNotification('Fee item updated successfully', 'success');
      setShowUpdateModal(false);
      resetForm();
      fetchFeeStructureAndItems();
    } catch (error) {
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await api.delete(`/api/v1/fee-structures/${id}/school/${user.school_id}/item/${selectedFeeItem.id}/delete`);
      showNotification('Fee item deleted successfully', 'success');
      setShowDeleteModal(false);
      fetchFeeStructureAndItems();
    } catch (error) {
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApiError = (error) => {
    if (error.response?.status === 422) {
      const { detail } = error.response.data;
      if (Array.isArray(detail)) {
        const errors = {};
        detail.forEach(err => {
          const field = err.loc[err.loc.length - 1];
          errors[field] = err.msg;
        });
        setFormErrors(errors);
        showNotification('Validation error. Please check your inputs.', 'error');
      } else if (typeof detail === 'string') {
        showNotification(detail, 'error');
      }
    } else {
      showNotification(error.response?.data?.detail || 'An error occurred', 'error');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', amount: '', is_mandatory: true });
    setFormErrors({});
  };

  const openUpdateModal = (feeItem) => {
    setSelectedFeeItem(feeItem);
    setFormData({
      name: feeItem.name,
      description: feeItem.description,
      amount: feeItem.amount.toString(),
      is_mandatory: feeItem.is_mandatory
    });
    setShowUpdateModal(true);
  };

  const openDeleteModal = (feeItem) => {
    setSelectedFeeItem(feeItem);
    setShowDeleteModal(true);
  };

  const filteredFeeItems = feeItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const calculateTotal = () => {
    return filteredFeeItems.reduce((sum, item) => sum + item.amount, 0);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    
    // Header
    doc.setFillColor(14, 82, 82);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    // School Name and Title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Fee Structure Report', pageWidth / 2, 20, { align: 'center' });
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(user.school_name || 'School Name', pageWidth / 2, 30, { align: 'center' });
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    
    // Fee Structure Information Box
    const infoStartY = 55;
    doc.setFillColor(245, 245, 245);
    doc.rect(margin, infoStartY, pageWidth - 2 * margin, 35, 'F');
    
    // Add border
    doc.setDrawColor(14, 82, 82);
    doc.setLineWidth(0.5);
    doc.rect(margin, infoStartY, pageWidth - 2 * margin, 35);
    
    // Fee Structure Details
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Fee Structure Details', margin + 5, infoStartY + 8);
    
    doc.setFont(undefined, 'normal');
    doc.text(`Structure Name: ${feeStructure?.name || 'N/A'}`, margin + 5, infoStartY + 18);
    doc.text(`Total Items: ${feeItems.length}`, margin + 5, infoStartY + 28);
    doc.text(`Generated Date: ${new Date().toLocaleDateString()}`, pageWidth - margin - 5, infoStartY + 18, { align: 'right' });
    doc.text(`Total Amount: KES ${calculateTotal().toLocaleString()}`, pageWidth - margin - 5, infoStartY + 28, { align: 'right' });
    
    // Fee Items Table
    const tableStartY = infoStartY + 45;
    
    // Table Header
    doc.setFillColor(14, 82, 82);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    
    const headers = [
      { text: '#', width: 15 },
      { text: 'Item Name', width: 50 },
      { text: 'Description', width: 70 },
      { text: 'Amount (KES)', width: 35 },
      { text: 'Status', width: 25 }
    ];
    
    let currentX = margin;
    headers.forEach(header => {
      doc.rect(currentX, tableStartY, header.width, 10, 'F');
      doc.text(header.text, currentX + header.width / 2, tableStartY + 7, { align: 'center' });
      currentX += header.width;
    });
    
    // Table Body
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
    
    let currentY = tableStartY + 10;
    feeItems.forEach((item, index) => {
      // Alternate row colors
      if (index % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(margin, currentY, pageWidth - 2 * margin, 10, 'F');
      }
      
      currentX = margin;
      
      // Row content
      doc.text((index + 1).toString(), currentX + headers[0].width / 2, currentY + 7, { align: 'center' });
      currentX += headers[0].width;
      
      doc.text(item.name, currentX + 2, currentY + 7);
      currentX += headers[1].width;
      
      // Truncate description if too long
      const desc = item.description.length > 40 ? item.description.substring(0, 37) + '...' : item.description;
      doc.text(desc, currentX + 2, currentY + 7);
      currentX += headers[2].width;
      
      doc.text(item.amount.toLocaleString(), currentX + headers[3].width - 2, currentY + 7, { align: 'right' });
      currentX += headers[3].width;
      
      // Status badge
      const status = item.is_mandatory ? 'Mandatory' : 'Optional';
      const statusColor = item.is_mandatory ? [220, 38, 38] : [34, 197, 94];
      doc.setFillColor(...statusColor);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.roundedRect(currentX + 2, currentY + 3, headers[4].width - 4, 6, 2, 2, 'F');
      doc.text(status, currentX + headers[4].width / 2, currentY + 7, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      
      currentY += 10;
      
      // Check if we need a new page
      if (currentY > pageHeight - 40) {
        doc.addPage();
        currentY = 20;
      }
    });
    
    // Total Summary Box
    const summaryY = currentY + 10;
    doc.setFillColor(14, 82, 82);
    doc.rect(margin, summaryY, pageWidth - 2 * margin, 15, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('Total Amount:', margin + 5, summaryY + 10);
    doc.text(`KES ${calculateTotal().toLocaleString()}`, pageWidth - margin - 5, summaryY + 10, { align: 'right' });
    
    // Footer
    doc.setTextColor(128, 128, 128);
    doc.setFontSize(8);
    doc.setFont(undefined, 'italic');
    doc.text('This is a system generated document', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    // Save the PDF
    doc.save(`fee-structure-${feeStructure?.name || 'report'}-${new Date().toISOString().split('T')[0]}.pdf`);
    showNotification('PDF exported successfully', 'success');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      {/* Notification Bar */}
      {notification.show && (
        <div className={`fixed top-16 left-1/2 transform -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg transition-all duration-300 ${
            notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}
        >
          {notification.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          <span className="font-medium">{notification.message}</span>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/fees-structure')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Fee Items</h1>
              <p className="text-gray-600 mt-1">Manage fee items for this structure</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={exportToPDF}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200"
              >
                <Download size={20} />
                Export PDF
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-all duration-200 hover:shadow-lg"
                style={{ backgroundColor: '#0E5252' }}
              >
                <Plus size={20} />
                Add Fee Item
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search fee items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg transition-all duration-200"
              style={{ focusRingColor: '#0E5252' }}
            />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Items</p>
                <p className="text-2xl font-bold text-gray-900">{feeItems.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="text-blue-600" size={24} />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Mandatory Items</p>
                <p className="text-2xl font-bold text-gray-900">
                  {feeItems.filter(item => item.is_mandatory).length}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="text-red-600" size={24} />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900">
                  KES {calculateTotal().toLocaleString()}
                </p>
              </div>
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#E6F4F4' }}>
                <span className="text-2xl" style={{ color: '#0E5252' }}>$</span>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin text-gray-400" size={40} />
            </div>
          ) : filteredFeeItems.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <AlertCircle size={48} className="mx-auto" />
              </div>
              <p className="text-gray-600 text-lg">No fee items found</p>
              <p className="text-gray-500 mt-2">Add your first fee item to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200" style={{ backgroundColor: '#0E5252' }}>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Item Name</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Description</th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-white uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-white uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredFeeItems.map((item, index) => (
                    <tr 
                      key={item.id} 
                      className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors duration-200`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{item.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900">KES {item.amount.toLocaleString()}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          item.is_mandatory 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {item.is_mandatory ? 'Mandatory' : 'Optional'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => openUpdateModal(item)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3 transition-colors duration-200"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => openDeleteModal(item)}
                          className="text-red-600 hover:text-red-900 transition-colors duration-200"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100">
                    <td colSpan="2" className="px-6 py-4 text-sm font-medium text-gray-900">Total</td>
                    <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                      KES {calculateTotal().toLocaleString()}
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Add Fee Item</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreateSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      formErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      formErrors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    rows="3"
                    required
                  />
                  {formErrors.description && <p className="text-red-500 text-xs mt-1">{formErrors.description}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (KES)</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      formErrors.amount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    min="0"
                    step="0.01"
                    required
                  />
                  {formErrors.amount && <p className="text-red-500 text-xs mt-1">{formErrors.amount}</p>}
                </div>
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.is_mandatory}
                      onChange={(e) => setFormData({ ...formData, is_mandatory: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 focus:ring-2"
                      style={{ accentColor: '#0E5252' }}
                    />
                    <span className="text-sm font-medium text-gray-700">Mandatory Item</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 rounded-lg text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#0E5252' }}
                >
                  {submitting && <Loader2 className="animate-spin" size={16} />}
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update Modal */}
      {showUpdateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Update Fee Item</h2>
              <button
                onClick={() => {
                  setShowUpdateModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleUpdateSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      formErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      formErrors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    rows="3"
                    required
                  />
                  {formErrors.description && <p className="text-red-500 text-xs mt-1">{formErrors.description}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (KES)</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      formErrors.amount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    min="0"
                    step="0.01"
                    required
                  />
                  {formErrors.amount && <p className="text-red-500 text-xs mt-1">{formErrors.amount}</p>}
                </div>
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.is_mandatory}
                      onChange={(e) => setFormData({ ...formData, is_mandatory: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 focus:ring-2"
                      style={{ accentColor: '#0E5252' }}
                    />
                    <span className="text-sm font-medium text-gray-700">Mandatory Item</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowUpdateModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 rounded-lg text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#0E5252' }}
                >
                  {submitting && <Loader2 className="animate-spin" size={16} />}
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Delete Fee Item</h2>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Are you sure you want to delete this fee item?
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                This action cannot be undone. The fee item "{selectedFeeItem?.name}" will be permanently removed.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="animate-spin" size={16} />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeStructureItems;